import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Preloader } from "../../../../components/Preloader";
import { getCourseLessonQuiz } from '../../api/getCourseLessonQuiz';
import { CardsContext } from '../../CardsContext';
import * as cl from './CourseLessonQuizz.module.scss';
import { sendPageViewEvent } from '../../../../utils/utils';
import './dark.scss';
import AppLink from '../../../../components/AppLink';

export function CourseLessonQuizz({ name }){
  const iframeRef = useRef(null);
  const { routes } = useContext(CardsContext);
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [height, setHeight] = useState('auto');
  const history = useHistory();

  const load = useCallback(async (name) => {
    try {
      setIsLoading(true);

      const data = await getCourseLessonQuiz(name);

      setState(data);

      const parser = new DOMParser();
      const doc = parser.parseFromString(`${data.data.post_name} – PokerCoaching.com`, 'text/html');
      document.title = doc.body.innerText;

      sendPageViewEvent(data.title);
    } catch(e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleTheme = () => {
    const isDarkTheme = document.body.classList.contains('pc-theme-dark');
    if (isDarkTheme) {
      iframeRef.current?.contentDocument?.body?.classList.add('pc-theme-dark');
    } else {
      iframeRef.current?.contentDocument?.body?.classList.remove('pc-theme-dark');
    }
  }

  useEffect(() => {
    const classObserver =  new MutationObserver((mutations) => {
      mutations.forEach(mu => {
        if (mu.type !== 'attributes' && mu.attributeName !== 'class') return
        handleTheme()
      })
    })
    const body = document.querySelector('body');
    classObserver.observe(body, { attributes: true })
  }, [])


  function setIframeHeight() {
    if(!iframeRef?.current?.contentDocument?.body) {
      return;
    }

    setHeight(iframeRef.current.contentDocument.body.scrollHeight + 'px');
  }

  function onIframeLoad() {
    setIframeHeight();
    handleTheme()

    if(!iframeRef?.current?.contentDocument) {
      setIsLoading(false);
      return;
    }

    iframeRef.current.contentDocument.body.style.backgroundColor = "transparent";
    setIsLoading(false);
  }

  useEffect(() => {
    load(name);
  }, [name, load]);

  useEffect(() => {
    function onPageResize() {
      setIframeHeight();
    }

    function onIframeMessage(e) {
      if(e.data.type !== 'ld-quiz'){
        return;
      }

      if(e.data.action === 'quiz_continue_link') {
        history.push(`${routes.home}${state.course.course_post_name}/${state.continue_link}/`);
        return;
      }

      if(e.data.action === 'back_to_course') {
        history.push(`${routes.home}${state.course.course_post_name}/`);
        return;
      }

      if(e.data.action === 'scroll_top') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        return;
      }
    }

    window.addEventListener('resize', onPageResize);
    window.addEventListener('message', onIframeMessage);

    const resizeIntervalId = setInterval(() => {
      setIframeHeight();
    }, 1000);

    return () => {
      window.removeEventListener('resize', onPageResize);
      window.removeEventListener('message', onIframeMessage);
      clearInterval(resizeIntervalId);
    };
  }, [history, routes, state]);

  if(error) {
    return (
      <div className="error">
        <p className="error__msg">{error}</p>
        <AppLink to={`${routes.home}`} className="error__btn">Back</AppLink>
      </div>
    );
  }

  return (
    <div className={cl.wrapper}>
      <div className="learndash-wrapper">
        <div style={{ display: isLoading ? 'none' : 'block' }}>
          {state && (
            <>
              <h1 className={cl.title} dangerouslySetInnerHTML={{ __html: state.data.title }} />

              <div className="ld-topic-status">
                <div className="ld-breadcrumbs">
                  <div className="ld-breadcrumbs-segments">
                    <span>
                      <AppLink to={`${routes.home}${state.course.course_post_name}/`}>
                        <span dangerouslySetInnerHTML={{ __html: state.course.course_title }} />
                      </AppLink>
                    </span>
                    <span>
                      <AppLink to={`${routes.home}${state.course.course_post_name}/${state.lesson.lesson_post_name}/`}>
                        <span dangerouslySetInnerHTML={{ __html: state.lesson.lesson_title }} />
                      </AppLink>
                    </span>
                    <span dangerouslySetInnerHTML={{ __html: state.data.title }} />
                  </div>
                </div>
              </div>

              <iframe
                ref={iframeRef}
                title={name}
                src={`${state.data.link}?_pc_webview=true`}
                className={cl.quizzIframe}
                style={{ height }}
                onLoad={onIframeLoad}
              />
            </>
          )}
        </div>

        {isLoading && (
          <Preloader className={cl.preloader} />
        )}
      </div>
    </div>
  );
}
